/**
 * Whether the Wall-E list decides, on the list's own page (DEC-28).
 *
 * Everyone signed in sees the state; an admin can change it. Turning it on is
 * confirmed with the number of entries that will decide from then on, because
 * from that moment those tokens are approved or refused here with nobody
 * asked. The change goes through PUT /settings like every switch, which writes
 * the switch_on or switch_off audit row.
 *
 * THE CONFIRMATION IS IN THE CARD, NOT A MODAL DIALOG. A Fluent Dialog mounts
 * in a portal, runs its motion and hands focus to tabster's modalizer, and the
 * first one opened on the slower pipeline agent was not found as a dialog
 * after the click (fc75312), while a later one in the same file was. Inline,
 * the question renders in the same update as the click, beside the control it
 * is about, and none of that machinery is between them.
 */

import { useCallback, useEffect, useState } from 'react'
import {
  Button,
  MessageBar,
  MessageBarActions,
  MessageBarBody,
  MessageBarTitle,
  Switch,
  makeStyles,
  tokens,
} from '@fluentui/react-components'
import { SectionCard, StatusBadge } from '../platform'
import { ApiError, api } from '../api'
import { problemText } from '../auth'

export const LIST_SWITCH = 'wall_e_list_enabled'

interface Switches {
  switches: { name: string; label: string; on: boolean; updated_at: number | null }[]
  takes_effect_within_seconds: number
}

const useStyles = makeStyles({
  row: { display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: tokens.spacingHorizontalL },
  muted: { color: tokens.colorNeutralForeground2, margin: 0 },
})

export function WallEListSwitch({
  isAdmin,
  accepted,
  blocked,
}: {
  isAdmin: boolean
  accepted: number
  blocked: number
}) {
  const styles = useStyles()
  const [state, setState] = useState<{ on: boolean; within: number } | null>(null)
  const [confirming, setConfirming] = useState(false)
  const [refused, setRefused] = useState<string | null>(null)

  const read = useCallback(() => {
    api
      .get<Switches>('/switches')
      .then((answer) => {
        const found = Array.isArray(answer?.switches)
          ? answer.switches.find((s) => s.name === LIST_SWITCH)
          : undefined
        setState(found ? { on: found.on, within: answer.takes_effect_within_seconds } : null)
      })
      .catch(() => setState(null))
  }, [])

  useEffect(() => {
    read()
  }, [read])

  const put = (on: boolean) => {
    setRefused(null)
    setConfirming(false)
    api
      .put('/settings', { switches: { [LIST_SWITCH]: on } })
      .then(read)
      .catch((e) => setRefused(e instanceof ApiError ? problemText(e) : String(e)))
  }

  if (!state) return null
  const deciding = accepted + blocked
  const within = `It reaches every authorization within ${state.within} seconds.`

  return (
    <SectionCard title="Whether the list decides">
      <div className={styles.row}>
        <StatusBadge
          status={state.on ? 'success' : 'info'}
          label={state.on ? 'Deciding' : 'Not deciding'}
        />
        <p className={styles.muted}>
          {state.on
            ? `${deciding} entries decide now: ${accepted} approved and ${blocked} refused at once, with nobody asked.`
            : `Off: the list is kept and read and decides nothing. On, ${deciding} entries would decide: ${accepted} approved and ${blocked} refused at once.`}
        </p>
        {isAdmin ? (
          <Switch
            label="Decide tokens on the Wall-E list"
            checked={state.on}
            onChange={(_, data) => (data.checked ? setConfirming(true) : put(false))}
          />
        ) : null}
      </div>
      {refused ? (
        <MessageBar intent="error">
          <MessageBarBody>Not changed: {refused}</MessageBarBody>
        </MessageBar>
      ) : null}
      {confirming ? (
        <div role="group" aria-label="Confirm turning the Wall-E list on">
          <MessageBar intent="warning" layout="multiline">
            <MessageBarBody>
              <MessageBarTitle>Let the Wall-E list decide?</MessageBarTitle>
              {deciding} tokens will be decided here from then on, with nobody asked: {accepted} approved
              and {blocked} refused. Every other token goes to DCS, TIM or Hubject as before. {within}
            </MessageBarBody>
            <MessageBarActions>
              <Button onClick={() => setConfirming(false)}>Cancel</Button>
              <Button appearance="primary" onClick={() => put(true)}>
                Let it decide
              </Button>
            </MessageBarActions>
          </MessageBar>
        </div>
      ) : null}
    </SectionCard>
  )
}
